import { API_PATH } from './config';
import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    Button,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

const dk = {
    bg: '#0d1117',
    card: '#161b22',
    elevated: '#21262d',
    hover: '#2d333b',
    border: 'rgba(99, 102, 241, 0.2)',
    borderHover: 'rgba(99, 102, 241, 0.4)',
    textPrimary: '#e6edf3',
    textSecondary: '#8b949e',
    textMuted: '#6e7681',
    accent: '#6366f1',
    accentLight: '#818cf8',
};

const Stat = ({ value, label, color = dk.accentLight }) => (
    <Card sx={{ bgcolor: dk.card, border: `1px solid ${dk.border}`, borderRadius: 2, boxShadow: 'none', height: '100%' }}>
        <CardContent sx={{ textAlign: 'center', py: 2.5 }}>
            <Typography variant="h4" sx={{ color, fontWeight: 700 }}>{value}</Typography>
            <Typography variant="body2" sx={{ color: dk.textSecondary, mt: 0.5 }}>{label}</Typography>
        </CardContent>
    </Card>
);

const BarChartDaily = ({ title, series, color = dk.accent }) => {
    const max = Math.max(1, ...series.map(d => d.count));
    return (
        <Paper sx={{ p: 3, bgcolor: dk.card, border: `1px solid ${dk.border}`, borderRadius: 2, boxShadow: 'none' }}>
            <Typography variant="h6" sx={{ color: dk.textPrimary, fontWeight: 700, mb: 2 }}>{title}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: 140 }}>
                {series.map((d) => (
                    <Tooltip key={d.date} title={`${d.date}: ${d.count}`} arrow>
                        <Box sx={{
                            flex: 1, minWidth: 3,
                            height: `${(d.count / max) * 100}%`,
                            minHeight: d.count > 0 ? 2 : 0,
                            bgcolor: color, borderRadius: '2px 2px 0 0',
                            opacity: d.count > 0 ? 0.9 : 0.15,
                            '&:hover': { opacity: 1 },
                        }} />
                    </Tooltip>
                ))}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="caption" sx={{ color: dk.textMuted }}>{series[0]?.date}</Typography>
                <Typography variant="caption" sx={{ color: dk.textMuted }}>{series[series.length - 1]?.date}</Typography>
            </Box>
        </Paper>
    );
};

const timeAgo = (s) => {
    if (!s) return '—';
    const d = new Date((s || '').replace(' ', 'T'));
    if (isNaN(d)) return '—';
    const secs = Math.floor((Date.now() - d.getTime()) / 1000);
    if (secs < 60) return 'Just now';
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
};

const COLUMNS = [
    { id: 'label', label: 'Function', numeric: false },
    { id: 'total', label: 'Total', numeric: true },
    { id: 'last_24h', label: '24h', numeric: true },
    { id: 'last_7d', label: '7d', numeric: true },
    { id: 'last_30d', label: '30d', numeric: true },
    { id: 'unique_users', label: 'Users', numeric: true },
    { id: 'last_used', label: 'Last Used', numeric: false },
];

const AdminFeatureUsage = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [orderBy, setOrderBy] = useState('total');
    const [order, setOrder] = useState('desc');

    const fetchUsage = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_PATH}/admin/feature-usage.php`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (res.status === 403) { setError('Access denied. Admins only.'); return; }
            if (res.status === 401) { setError('Please log in to view usage.'); return; }
            const json = await res.json();
            if (json.success) setData(json);
            else setError(json.error || 'Failed to load feature usage.');
        } catch (e) {
            setError('Failed to connect to server: ' + e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchUsage(); }, []);

    const handleSort = (id) => {
        if (orderBy === id) setOrder(order === 'asc' ? 'desc' : 'asc');
        else { setOrderBy(id); setOrder(id === 'label' ? 'asc' : 'desc'); }
    };

    const sortedFeatures = useMemo(() => {
        if (!data) return [];
        const rows = [...data.features];
        rows.sort((a, b) => {
            let va = a[orderBy], vb = b[orderBy];
            if (orderBy === 'label') { va = (va || '').toLowerCase(); vb = (vb || '').toLowerCase(); }
            if (va < vb) return order === 'asc' ? -1 : 1;
            if (va > vb) return order === 'asc' ? 1 : -1;
            return 0;
        });
        return rows;
    }, [data, orderBy, order]);

    const maxTotal = useMemo(
        () => (data ? Math.max(1, ...data.features.map(f => f.total)) : 1),
        [data]
    );

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ color: dk.textPrimary, fontWeight: 700 }}>
                        📊 Feature Usage
                    </Typography>
                    <Typography variant="caption" sx={{ color: dk.textMuted }}>
                        How often each app function is used (per authenticated API call).
                        {data && ` · Generated ${data.generated_at}`}
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <RefreshIcon />}
                    onClick={fetchUsage}
                    disabled={loading}
                    sx={{ bgcolor: dk.accent, '&:hover': { bgcolor: '#4f46e5' }, '&.Mui-disabled': { bgcolor: dk.elevated, color: dk.textMuted } }}
                >
                    Refresh
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3, bgcolor: 'rgba(239,68,68,0.1)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)', '& .MuiAlert-icon': { color: '#f87171' } }}>
                    {error}
                </Alert>
            )}

            {loading && !data && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                    <CircularProgress sx={{ color: dk.accent }} />
                </Box>
            )}

            {data && (
                <>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={6} sm={4} md={2.4}><Stat value={data.totals.events_total} label="Total Events" /></Grid>
                        <Grid item xs={6} sm={4} md={2.4}><Stat value={data.totals.events_24h} label="Last 24h" color="#10b981" /></Grid>
                        <Grid item xs={6} sm={4} md={2.4}><Stat value={data.totals.events_7d} label="Last 7 days" color="#06b6d4" /></Grid>
                        <Grid item xs={6} sm={4} md={2.4}><Stat value={data.totals.events_30d} label="Last 30 days" color="#f59e0b" /></Grid>
                        <Grid item xs={6} sm={4} md={2.4}><Stat value={data.totals.distinct_features} label="Functions Used" color="#a855f7" /></Grid>
                    </Grid>

                    <Box sx={{ mb: 2 }}>
                        <BarChartDaily title="Total activity — last 30 days" series={data.daily} color="#6366f1" />
                    </Box>

                    <TableContainer component={Paper} sx={{ bgcolor: dk.card, border: `1px solid ${dk.border}`, borderRadius: 2, boxShadow: 'none', overflow: 'hidden' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: dk.elevated }}>
                                    {COLUMNS.map((col) => (
                                        <TableCell
                                            key={col.id}
                                            align={col.numeric ? 'right' : 'left'}
                                            sortDirection={orderBy === col.id ? order : false}
                                            sx={{ color: dk.textSecondary, borderBottom: `1px solid ${dk.border}`, fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}
                                        >
                                            <TableSortLabel
                                                active={orderBy === col.id}
                                                direction={orderBy === col.id ? order : 'asc'}
                                                onClick={() => handleSort(col.id)}
                                                sx={{
                                                    color: `${dk.textSecondary} !important`,
                                                    '& .MuiTableSortLabel-icon': { color: `${dk.accentLight} !important` },
                                                    '&.Mui-active': { color: `${dk.textPrimary} !important` },
                                                }}
                                            >
                                                {col.label}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedFeatures.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 5, borderBottom: `1px solid ${dk.border}` }}>
                                            <Typography sx={{ color: dk.textSecondary }}>No usage recorded yet.</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    sortedFeatures.map((f) => (
                                        <TableRow key={f.feature} sx={{ '&:hover': { bgcolor: dk.hover }, '& td': { borderBottom: `1px solid ${dk.border}` } }}>
                                            <TableCell sx={{ minWidth: 200 }}>
                                                <Typography variant="body2" sx={{ color: dk.textPrimary, fontWeight: 600 }}>{f.label}</Typography>
                                                {/* usage bar relative to the most-used function */}
                                                <Box sx={{ mt: 0.5, height: 5, bgcolor: dk.elevated, borderRadius: 3, overflow: 'hidden' }}>
                                                    <Box sx={{ width: `${(f.total / maxTotal) * 100}%`, height: '100%', bgcolor: dk.accent, borderRadius: 3 }} />
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right"><Typography variant="body2" sx={{ color: dk.textPrimary, fontWeight: 700 }}>{f.total}</Typography></TableCell>
                                            <TableCell align="right"><Typography variant="body2" sx={{ color: dk.textSecondary }}>{f.last_24h}</Typography></TableCell>
                                            <TableCell align="right"><Typography variant="body2" sx={{ color: dk.textSecondary }}>{f.last_7d}</Typography></TableCell>
                                            <TableCell align="right"><Typography variant="body2" sx={{ color: dk.textSecondary }}>{f.last_30d}</Typography></TableCell>
                                            <TableCell align="right"><Typography variant="body2" sx={{ color: dk.accentLight }}>{f.unique_users}</Typography></TableCell>
                                            <TableCell><Typography variant="caption" sx={{ color: dk.textMuted }}>{timeAgo(f.last_used)}</Typography></TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </Box>
    );
};

export default AdminFeatureUsage;
